/*
 * RivanyOS Pixel — public/pixel.js
 *
 * Standalone, zero-dependency visitor-journey tracker for the May Anthony
 * Shopify storefronts (mayanthony.com / officialmay.myshopify.com = INT).
 * Embedded via theme.liquid — see docs/pixel-installation.md.
 *
 * Captures page views, product views, add-to-cart and purchase, with UTM /
 * fbclid / ad_id / campaign_id / adset_id / referrer per touchpoint, and POSTs
 * them to the RivanyOS collector. It NEVER throws and NEVER blocks render —
 * every external touch is wrapped in try/catch and the work is deferred to
 * idle time.
 */
(function () {
  'use strict'

  var ENDPOINT = 'https://rivanyos.vercel.app/api/pixel-event'
  var RECORD_ENDPOINT = 'https://rivanyos.vercel.app/api/record-session'
  var REPLAY_CONFIG_ENDPOINT = 'https://rivanyos.vercel.app/api/session-replay-config'

  // ── session replay (rrweb) state — all per page-load, all guarded ─────────────
  var cartValue = null
  var replayEvents = []
  var replayRecording = false
  var replayStop = null
  var replayStart = Date.now()
  var replayInactivity = null
  var replayBound = false

  // ── tiny storage + uuid helpers (all guarded) ────────────────────────────────
  function uuid() {
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0
      var v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
  function lsGet(k) { try { return window.localStorage.getItem(k) } catch (e) { return null } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v) } catch (e) {} }
  function lsDel(k) { try { window.localStorage.removeItem(k) } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k) } catch (e) { return null } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v) } catch (e) {} }

  // ── identity ──────────────────────────────────────────────────────────────────
  // visitor_id: persists across sessions (localStorage). session_id: per tab/session.
  function visitorId() {
    var id = lsGet('rv_visitor_id')
    if (!id) { id = uuid(); lsSet('rv_visitor_id', id) }
    return id
  }
  function sessionId() {
    var id = ssGet('rv_session_id')
    if (!id) { id = uuid(); ssSet('rv_session_id', id) }
    return id
  }

  // ── URL params ────────────────────────────────────────────────────────────────
  function params() {
    try { return new URLSearchParams(window.location.search) } catch (e) { return new URLSearchParams('') }
  }

  // UTMs are captured on the landing hit and held for the whole session, so a
  // visitor who navigates away from the landing URL still carries attribution.
  function utms() {
    var stored = ssGet('rv_utms')
    if (stored) { try { return JSON.parse(stored) } catch (e) {} }
    var p = params()
    var u = {
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term')
    }
    if (u.utm_source || u.utm_medium || u.utm_campaign || u.utm_content || u.utm_term) {
      ssSet('rv_utms', JSON.stringify(u))
    }
    return u
  }

  // fb click context is windowed so a click on day 1 still attributes a purchase
  // on day N. 30 days clears the observed 8-day #9869 click-to-purchase cycle,
  // aligns with Meta's 28-day click-attribution precedent, and is recent enough
  // to avoid attributing a stale, months-old click to today's purchase.
  var RV_FB_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

  // fbclid / ad_id / campaign_id / adset_id — persisted in localStorage (NOT
  // sessionStorage) with a time window, so the context survives across sessions.
  // A fresh click (any fb param in the current URL) overwrites the stored context
  // and resets the window (last-touch). rv_fb moved from sessionStorage to
  // localStorage — separate stores, so any pre-existing sessionStorage rv_fb is
  // simply ignored and dies on tab close; no cleanup needed. rv_utms is left
  // session-scoped and untouched.
  function fbData() {
    try {
      var p = params()
      var fresh = {
        fbclid: p.get('fbclid'),
        ad_id: p.get('ad_id'),
        campaign_id: p.get('campaign_id'),
        adset_id: p.get('adset_id')
      }
      // Fresh click wins: any fb param present overwrites + resets the window.
      if (fresh.fbclid || fresh.ad_id || fresh.campaign_id || fresh.adset_id) {
        lsSet('rv_fb', JSON.stringify({ data: fresh, ts: Date.now() }))
        return fresh
      }
      // No fresh params: reuse the stored click only if still within the window.
      var stored = lsGet('rv_fb')
      if (stored) {
        var parsed = JSON.parse(stored)
        if (parsed && parsed.data && typeof parsed.ts === 'number' && (Date.now() - parsed.ts) <= RV_FB_WINDOW_MS) {
          return parsed.data
        }
        lsDel('rv_fb') // absent data or expired window → drop the stale key
      }
      return { fbclid: null, ad_id: null, campaign_id: null, adset_id: null }
    } catch (e) {
      return { fbclid: null, ad_id: null, campaign_id: null, adset_id: null }
    }
  }

  // ── store detection ─────────────────────────────────────────────────────────
  // officialmay.myshopify.com and mayanthony.com are the INT store. A future NGA
  // storefront whose hostname contains "nga" resolves to NGA.
  function store() {
    var h = ''
    try { h = (window.location.hostname || '').toLowerCase() } catch (e) {}
    if (h.indexOf('nga') !== -1) return 'NGA'
    return 'INT'
  }

  // ── product handle from /products/<handle> ────────────────────────────────────
  function productHandle() {
    try {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/)
      return m ? m[1] : null
    } catch (e) { return null }
  }

  function safe(fn) { try { return fn() } catch (e) { return null } }

  // ── cart stitching ──────────────────────────────────────────────────────────
  // Persist visitor/session id onto the Shopify cart as attributes so they carry
  // through to the order and are readable from the checkout_completed web pixel
  // via checkout.attributes. Cross-origin localStorage is NOT shared with the
  // checkout origin, so the cart is the reliable bridge. Guarded to one write
  // per session to avoid spamming /cart/update.js on every page view.
  function stampCart() {
    try {
      // Cache the live cart total for the replay session meta. Refreshed on every
      // stampCart() call (page load + add-to-cart) since the cart changes over time.
      try {
        var rootC = '/'
        try { if (window.Shopify && Shopify.routes && Shopify.routes.root) rootC = Shopify.routes.root } catch (e) {}
        fetch(rootC + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json() })
          .then(function (c) { if (c && typeof c.total_price === 'number') cartValue = c.total_price / 100 })['catch'](function () {})
      } catch (e) {}
      if (ssGet('rv_cart_stamped') === '1') return
      var root = '/'
      try { if (window.Shopify && Shopify.routes && Shopify.routes.root) root = Shopify.routes.root } catch (e) {}
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            rv_visitor_id: visitorId(),
            rv_session_id: sessionId()
          }
        })
      }).then(function () { ssSet('rv_cart_stamped', '1') })['catch'](function () {})
    } catch (e) {}
  }

  // ── send ──────────────────────────────────────────────────────────────────────
  function sendEvent(eventType, extra, onAccepted) {
    try {
      var u = utms()
      var f = fbData()
      var payload = {
        visitor_id: visitorId(),
        session_id: sessionId(),
        event_type: eventType,
        page_url: safe(function () { return window.location.href }),
        page_title: safe(function () { return document.title }),
        referrer: safe(function () { return document.referrer || null }),
        utm_source: u.utm_source || null,
        utm_medium: u.utm_medium || null,
        utm_campaign: u.utm_campaign || null,
        utm_content: u.utm_content || null,
        utm_term: u.utm_term || null,
        fbclid: f.fbclid || null,
        ad_id: f.ad_id || null,
        campaign_id: f.campaign_id || null,
        adset_id: f.adset_id || null,
        product_handle: null,
        product_title: null,
        customer_email: null,
        order_id: null,
        store: store()
      }
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]
        }
      }

      var body = JSON.stringify(payload)
      // Gated send: when a caller passes onAccepted, use fetch (sendBeacon has
      // no readable response) and invoke the callback only when the collector
      // accepts the event ({ ok: true }). Rejection, network error, or
      // exception: no callback, silently. Used only by the initial page_view.
      if (onAccepted) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: body,
            keepalive: true,
            mode: 'cors'
          }).then(function (r) { return r.json() })
            .then(function (j) { if (j && j.ok === true) onAccepted() })
            ['catch'](function () {})
        } catch (e) {}
        return
      }
      // text/plain keeps this a CORS "simple request" (no preflight) for both transports.
      var sent = false
      try {
        if (navigator.sendBeacon) {
          sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
        }
      } catch (e) { sent = false }
      if (!sent) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: body,
            keepalive: true,
            mode: 'cors'
          })['catch'](function () {})
        } catch (e) {}
      }
    } catch (e) { /* never throw — the storefront must be unaffected */ }
  }

  // ── session replay (rrweb) ────────────────────────────────────────────────────
  // Records the DOM only after a visitor reaches a product page (conditional
  // sampling), masks all inputs, buffers events in memory, and flushes the blob to
  // the collector on pagehide or after 30s of inactivity. Like everything else
  // here it never throws and never blocks the storefront.
  function loadRrweb(cb) {
    if (window.rrweb || window.rrwebRecord) return cb()
    try {
      // Derive the theme assets base from this pixel's own <script> src,
      // so rrweb-record.js loads from the same Shopify assets directory.
      var base = ''
      try {
        var sc = document.querySelector('script[src*="pixel.js"]')
        if (sc && sc.src) base = sc.src.replace(/pixel\.js(\?.*)?$/, '')
      } catch (e) {}
      var url = base ? (base + 'rrweb-record.js') : 'rrweb-record.js'
      var s = document.createElement('script')
      s.src = url
      s.onload = cb
      s.onerror = function () {}
      document.head.appendChild(s)
    } catch (e) {}
  }

  function replayPageCount() {
    var n = parseInt(ssGet('rv_page_count') || '0', 10)
    return isNaN(n) ? 0 : n
  }

  function replayDeviceType() {
    try {
      var w = (window.screen && screen.width) ? screen.width : window.innerWidth
      return w < 768 ? 'mobile' : (w < 1024 ? 'tablet' : 'desktop')
    } catch (e) { return 'desktop' }
  }

  function postRecord(body) {
    // Large payloads (e.g. the rrweb FullSnapshot, often >64KB) exceed the
    // ~64KB cap on sendBeacon AND keepalive fetch, which silently drop them.
    // A normal fetch (no keepalive) has no size cap — use it for large bodies.
    // Small bodies keep beacon/keepalive so they survive page unload.
    var LARGE = 60000 // ~60KB, safely under the 64KB beacon/keepalive cap
    try {
      if (body.length > LARGE) {
        fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, mode: 'cors' })['catch'](function () {})
        return
      }
    } catch (e) {}
    var sent = false
    try {
      if (navigator.sendBeacon) sent = navigator.sendBeacon(RECORD_ENDPOINT, new Blob([body], { type: 'text/plain' }))
    } catch (e) { sent = false }
    if (!sent) {
      try {
        fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' })['catch'](function () {})
      } catch (e) {}
    }
  }

  function flushReplay() {
    try {
      if (!replayEvents.length) return
      var payload = {
        action: 'save',
        session_id: sessionId(),
        visitor_id: visitorId(),
        events: replayEvents,
        meta: {
          entry_page: ssGet('rv_entry_page') || safe(function () { return window.location.href }),
          exit_page: safe(function () { return window.location.href }),
          page_count: replayPageCount(),
          duration_seconds: Math.round((Date.now() - replayStart) / 1000),
          device_type: replayDeviceType(),
          cart_value: cartValue,
          converted: false,
          store: store()
        }
      }
      postRecord(JSON.stringify(payload))
      // Clear the buffer so the next flush only carries new events — the server
      // appends each flush onto the stored blob (multi-page sessions accumulate).
      replayEvents = []
    } catch (e) {}
  }

  function resetReplayInactivity() {
    try {
      if (replayInactivity) clearTimeout(replayInactivity)
      replayInactivity = setTimeout(function () { flushReplay() }, 30000)
    } catch (e) {}
  }

  function startReplay() {
    if (replayRecording) return
    replayRecording = true
    loadRrweb(function () {
      try {
        var rec = (window.rrweb && window.rrweb.record) ? window.rrweb.record
                  : (window.rrwebRecord && window.rrwebRecord.record) ? window.rrwebRecord.record
                  : (typeof window.rrwebRecord === 'function') ? window.rrwebRecord
                  : null
        if (!rec) { replayRecording = false; return }
        replayStart = Date.now()
        replayStop = rec({
          emit: function (event) {
            try { replayEvents.push(event); resetReplayInactivity() } catch (e) {}
          },
          maskAllInputs: true,
          maskTextSelector: 'input, textarea, select'
        })
        // Early flush: rrweb emits Meta + FullSnapshot at the start. Persist them
        // ~2s in via the large-payload (no-keepalive) fetch while the page is
        // definitely alive, so the snapshot isn't lost on a fast bounce.
        try { setTimeout(flushReplay, 2000) } catch (e) {}
        if (!replayBound) {
          replayBound = true
          try { window.addEventListener('pagehide', flushReplay) } catch (e) {}
          try {
            document.addEventListener('visibilitychange', function () {
              if (document.visibilityState === 'hidden') flushReplay()
            })
          } catch (e) {}
        }
      } catch (e) { replayRecording = false }
    })
  }

  // Fail open on IP/country exclusion checks: record unless the visitor's own
  // IP/country is explicitly excluded (the server-side collector enforces
  // exclusions authoritatively too). Fail quiet on a config-fetch failure or on
  // recording_enabled === false: do not start recording.
  function maybeStartReplay() {
    if (replayRecording) return
    try {
      fetch(REPLAY_CONFIG_ENDPOINT, { mode: 'cors' })
        .then(function (r) { return r.json() })
        .then(function (cfg) {
          try {
            if (cfg && cfg.recording_enabled === false) return
            var ips = (cfg && cfg.excluded_ips) || []
            var countries = (cfg && cfg.excluded_countries) || []
            if (cfg && cfg.ip && ips.indexOf(cfg.ip) !== -1) return
            if (cfg && cfg.country && countries.indexOf(cfg.country) !== -1) return
          } catch (e) {}
          startReplay()
        })['catch'](function () {})
    } catch (e) {}
  }

  // ── public API ──────────────────────────────────────────────────────────────
  // Called from the theme (add-to-cart) and the order confirmation page (purchase).
  // Guarded for `window` so this file is safe in the Customer Events sandbox too
  // (a Web Worker context where `window` may be undefined).
  var RivanyPixel = {
    addToCart: function (productHandleArg, productTitleArg) {
      // Stamp the cart at add-to-cart time too, so the id is on the cart even if
      // the page-load stamp was skipped or hadn't completed yet.
      stampCart()
      sendEvent('add_to_cart', {
        product_handle: productHandleArg || productHandle(),
        product_title: productTitleArg || null
      })
    },
    purchase: function (orderId, customerEmail) {
      sendEvent('purchase', {
        order_id: orderId != null && orderId !== '' ? String(orderId) : null,
        customer_email: customerEmail || null
      })
      // Mark the replay session converted (sets converted=true + shopify_order_id).
      try {
        postRecord(JSON.stringify({
          action: 'convert',
          session_id: sessionId(),
          order_id: orderId != null && orderId !== '' ? String(orderId) : null,
          customer_email: customerEmail || null
        }))
      } catch (e) {}
    }
  }
  try { if (typeof window !== 'undefined') window.RivanyPixel = RivanyPixel } catch (e) {}

  // ── Customer Events sandbox (Shopify Settings → Customer Events custom pixel) ─
  // When this file is registered as a Customer Events pixel, the sandbox exposes
  // `analytics`. In that mode we ONLY subscribe to checkout_started and forward it
  // to the collector (funnel step 4); the theme-context boot() is skipped. The
  // visitor/session ids come from the cart attributes stamped by stampCart(),
  // mirroring how the checkout_completed pixel reads them.
  function inCustomerEvents() {
    try { return typeof analytics !== 'undefined' && analytics && typeof analytics.subscribe === 'function' } catch (e) { return false }
  }

  function bootCustomerEvents() {
    try {
      analytics.subscribe('checkout_started', function (event) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          // Same ck.email read as checkout_completed below — available this early too.
          var email = (ck && ck.email) ? ck.email : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'checkout_started',
            customer_email: email,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' })['catch'](function () {}) } catch (e) {}
          }
        } catch (e) {}
      })

      analytics.subscribe('checkout_contact_info_submitted', function (event) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          // Earliest Customer Events checkout event where Shopify populates
          // checkout.email for anonymous shoppers (checkout_started is structurally
          // too early — 0/205 historical checkout_started rows ever carried one).
          var email = (ck && ck.email) ? ck.email : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'checkout_contact_info_submitted',
            customer_email: email,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' })['catch'](function () {}) } catch (e) {}
          }
        } catch (e) {}
      })

      analytics.subscribe('checkout_completed', function (event) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          var rawId = (ck && ck.order && ck.order.id) ? String(ck.order.id) : null
          var orderId = rawId ? rawId.replace(/^gid:\/\/shopify\/Order\//, '') : null
          var email = (ck && ck.email) ? ck.email : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'purchase',
            order_id: orderId,
            customer_email: email,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' })['catch'](function () {}) } catch (e) {}
          }
          // Mark session replay as converted
          try {
            var convertBody = JSON.stringify({
              action: 'convert',
              session_id: sId || 'unknown',
              order_id: orderId,
              customer_email: email
            })
            var cSent = false
            try {
              if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                cSent = navigator.sendBeacon(RECORD_ENDPOINT, new Blob([convertBody], { type: 'text/plain' }))
              }
            } catch (e) { cSent = false }
            if (!cSent) {
              try { fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: convertBody, keepalive: true, mode: 'cors' })['catch'](function () {}) } catch (e) {}
            }
          } catch (e) {}
        } catch (e) {}
      })
    } catch (e) {}
  }

  // ── auto-fire on load (deferred so it never blocks render) ────────────────────
  function boot() {
    // Per-session page counter + entry page, used in the replay session meta.
    try {
      ssSet('rv_page_count', String(replayPageCount() + 1))
      if (!ssGet('rv_entry_page')) ssSet('rv_entry_page', safe(function () { return window.location.href }) || '')
    } catch (e) {}
    // Bot gate layer 1: replay starts only after this page_view is accepted by
    // the collector. On rejection, network error, or exception the recorder
    // never starts, silently.
    sendEvent('page_view', {}, function () { maybeStartReplay() })
    stampCart()
    // Listen for Prestige theme's variant:add event (fires on every successful
    // add-to-cart via fetch to /cart/add.js, including quick-buy and PDP).
    try {
      document.addEventListener('variant:add', function (e) {
        try {
          var item = e && e.detail && e.detail.items && e.detail.items[0]
          var handle = (item && item.handle) ? item.handle : productHandle()
          var title = (item && item.product_title) ? item.product_title : null
          stampCart()
          RivanyPixel.addToCart(handle, title)
        } catch (err) {}
      })
    } catch (e) {}
    var ph = productHandle()
    if (ph) {
      sendEvent('product_view', {
        product_handle: ph,
        product_title: safe(function () { return document.title })
      })
    }
  }
  try {
    if (inCustomerEvents()) {
      bootCustomerEvents()
    } else if (typeof window !== 'undefined' && window.requestIdleCallback) {
      requestIdleCallback(boot)
    } else {
      setTimeout(boot, 0)
    }
  } catch (e) {
    try { setTimeout(boot, 0) } catch (e2) {}
  }
})()
